import { FC } from 'react';
import ScrollAnimation from 'react-animate-on-scroll';
import styled from 'styled-components';

import { getImage } from '@/lib/fetchAPI';
import { parseHTML } from '@/lib/parse';

const ChatbotPricing: FC<any> = ({ data }) => (
	<>
		<PricingGrad src="/images/chatbots/PricingBg.png" />

		<Wrapper>
			<ScrollAnimation animateOnce animateIn="fadeInUp">
				<Title>{data?.title}</Title>

				<Text>{parseHTML(data?.text)}</Text>

				<Subtitle>{data?.subtitle}</Subtitle>
			</ScrollAnimation>

			<Main>
				<ScrollAnimation animateOnce animateIn="fadeInUp">
					<Blocks>
						{data?.pricingCBBlock?.map((item, index) => (
							<Block key={`cb_pricing_block_${index}`}>
								<BlockIcon alt={item.title} src={getImage(item.icon)} />

								<BlockTitle>{parseHTML(item.title)}</BlockTitle>

								<BlockText>{parseHTML(item.text)}</BlockText>

								{item.note ? <BlockNote>{item.note}</BlockNote> : null}
							</Block>
						))}
					</Blocks>
				</ScrollAnimation>

				<ScrollAnimation animateOnce animateIn="fadeInUp">
					<Bottom>
						{data?.noteRich ? (
							<BottomNote>{parseHTML(data?.noteRich)}</BottomNote>
						) : null}

						{data?.bottomTitle ? (
							<BottomTitle>{data?.bottomTitle}</BottomTitle>
						) : null}

						{data?.bottomText ? (
							<BottomText>{parseHTML(data?.bottomText)}</BottomText>
						) : null}
					</Bottom>
				</ScrollAnimation>
			</Main>
		</Wrapper>
	</>
);

const PricingGrad = styled.img`
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	z-index: -100;

	@media (max-width: 767px) {
		width: 110%;
	}
`;

const Wrapper = styled.div`
	width: 960px;
	max-width: 100%;
	margin: 120px auto 0;
	text-align: center;

	@media (max-width: 767px) {
		margin-top: 50px;
	}
`;

const Title = styled.div`
	font-family: var(--font-sans);
	font-weight: var(--font-bold);
	font-size: 42px;
	line-height: 50px;
	margin-bottom: 20px;
	color: var(--color-purple);

	@media (max-width: 767px) {
		font-size: 28px;
		line-height: 32px;
		margin-bottom: 15px;
	}
`;

const Text = styled.div`
	font-family: var(--font-sans);

	font-size: 18px;
	line-height: 25px;

	@media (max-width: 767px) {
		font-size: 16px;
		line-height: 25px;
		padding: 0 15px;
	}
`;

const Subtitle = styled.div`
	font-family: var(--font-sans);

	font-size: 28px;
	line-height: 125%;
	text-align: center;
	color: var(--color-purple);
	margin-top: 60px;

	@media (max-width: 767px) {
		font-size: 20px;
		margin-top: 30px;
	}
`;

const Main = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	position: relative;
	padding: 0 120px;
	margin-top: 140px;

	@media (max-width: 1128px) {
		padding: 0 20px;

		.fadeInUp {
			max-width: 100%;
		}
	}

	@media (max-width: 767px) {
		margin-top: 88px;
	}
`;

const Blocks = styled.div`
	display: flex;
	column-gap: 60px;
	position: relative;
	margin-top: -80px;

	@media (max-width: 767px) {
		flex-direction: column;
		column-gap: 0;
		margin-top: -56px;
		row-gap: 15px;
	}
`;

const Block = styled.div`
	width: 450px;
	background: linear-gradient(90deg, #7362f0 0%, var(--color-purple-40) 100%);
	border-radius: 50px;
	padding: 40px 15px;
	color: var(--color-white);

	@media (max-width: 767px) {
		width: 315px;
		padding: 32px 12px;
	}
`;

const BlockIcon = styled.img`
	width: 36px;
`;

const BlockTitle = styled.div`
	font-family: var(--font-sans);

	font-size: 28px;
	line-height: 35px;
	margin: 32px 0 15px;

	@media (max-width: 767px) {
		font-size: 22px;
		line-height: 30px;
		margin-top: 17px;
	}
`;

const BlockText = styled.div`
	font-family: var(--font-sans);

	font-size: 16px;
	line-height: 24px;

	@media (max-width: 767px) {
		font-size: 14px;
		line-height: 18px;
	}
`;

const BlockNote = styled.div`
	width: 88%;
	margin: 24px auto 0;
	padding: 18px 10px;
	border: 1px solid var(--color-white-80);
	border-radius: 20px;
	font-family: var(--font-sans);
	font-weight: var(--font-medium);
	font-size: 14px;
	line-height: 143%;
	text-align: center;
	color: var(--color-white);

	@media (max-width: 767px) {
		font-size: 12px;
		padding: 10px;
	}
`;

const Bottom = styled.div`
	margin-top: 60px;
	position: relative;

	@media (max-width: 767px) {
		margin-top: 40px;
	}
`;

const BottomNote = styled.div`
	padding: 20px 20px 20px 56px;
	max-width: 720px;
	background: var(--color-white) url(/images/chatbots/attention.svg) no-repeat
		top 22px left 22px / 20px;
	background-color: var(--color-white);
	box-shadow: 0px 10px 30px rgba(34, 23, 106, 0.08);
	border-radius: 18px;
	text-align: left;
	font-family: var(--font-sans);
	font-weight: var(--font-medium);
	font-size: 14px;
	line-height: 143%;
	color: var(--color-purple-50);

	a {
		color: var(--color-purple);
		text-decoration: none;
	}
`;

const BottomTitle = styled.div`
	font-family: var(--font-sans);

	font-size: 28px;
	line-height: 35px;
	color: var(--color-purple);
	margin-bottom: 16px;

	@media (max-width: 767px) {
		margin-bottom: 15px;
		font-size: 22px;
		line-height: 30px;
	}
`;

const BottomText = styled.div`
	font-family: var(--font-sans);

	font-size: 18px;
	line-height: 25px;

	@media (max-width: 767px) {
		font-size: 16px;
	}
`;

export default ChatbotPricing;
